import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import clone from 'lodash/clone';
import orderBy from 'lodash/orderBy';
import { CSSTransition } from 'react-transition-group';
import './styles.css';
import Avatar from '../../../../components/Avatar';
import BasicAccordion from '../../../../components/BasicAccordion';
import BasicButton from '../../../../components/BasicButton';
import BasicButtonGroup from '../../../../components/BasicButtonGroup';
import BasicCheckbox from '../../../../components/BasicCheckbox';
import BasicSubmitButton from '../../../../components/BasicSubmitButton';
import HorizontalLoader from '../../../../components/HorizontalLoader';
import LoadMask from '../../../../components/LoadMask';
import {ANIMATION_TIMEOUT_APPEAR} from '../../../../constants/animation';
import ENDPOINT from '../../../../constants/endpoints';
import {STUDENT_ROLE_ID} from '../../../../constants/roles';
import useApi from '../../../../hooks/useApi';

const CandidateSetTagForm = props => {
  const {
    params: {
      item: {
        CandidateID,
        FullName,
        PictureUrl,
        FirmName,
        SchoolName,
        OfficeLocationName,
        RoleID,
        currentSchoolName,
      },
    },
    actions: { proceed, dismiss, handleError },
  } = props;

  const _fieldPref = 'tag_id';

  const api = useApi();
  const [state, setState] = useState({ init: false });

  useEffect(() => {
    api.get(
      ENDPOINT.FORM.GET.TAG_PROFILE,
      { id: CandidateID },
      {
        handleSuccess: data => {
          setState({
            init: true,
            ...prepareData(data),
          });
        },
      }
    );
  }, []);

  const prepareData = data => {
    let groups = [];
    let selected = {};

    data.options.tags.forEach(item => {
      const groupId = item.attrs.TagTypeID;
      if (!groups.find(group => group.id === groupId)) {
        groups.push({
          label: item.attrs.TagTypeName,
          id: groupId,
          tags: [],
        });
      }
    });

    groups = groups.map(group => {
      return {
        ...group,
        tags: data.options.tags
          .filter(tag => tag.attrs.TagTypeID === group.id)
          .map(tag => ({
            id: tag.value,
            label: tag.label,
            disabled: tag.attrs.Readonly,
          })),
      };
    });

    data.options.tags.forEach(tag => {
      data.fields.tags &&
      data.fields.tags.length > 0 &&
      data.fields.tags.indexOf(tag.value) !== -1
        ? (selected[`${_fieldPref}${tag.value}`] = true)
        : (selected[`${_fieldPref}${tag.value}`] = false);
    });

    return {
      groups: orderBy(groups, ['label']),
      selected,
    };
  };

  const handleSubmit = (values, { setSubmitting, setErrors }) => {
    const preparedValues = clone(values);

    Object.keys(preparedValues).forEach(field => {
      let data = preparedValues[field];
      if (!data) {
        delete preparedValues[field];
      }
    });

    const reg = new RegExp(`^${_fieldPref}`);
    const tags = Object.keys(preparedValues).map(key => key.replace(reg, ''));
    const postParams = { id: CandidateID };
    if (tags.length) {
      postParams.tags = tags;
    }

    api.post(ENDPOINT.FORM.SAVE.TAG_PROFILE, postParams, {
      handleError: response => {
        setSubmitting(false);
        handleError(response, setErrors);
      },
      handleSuccess: () => proceed(),
    });
  };

  const isStudent = Number(RoleID) === STUDENT_ROLE_ID;
  const [toggledIndex, setToggledIndex] = useState(null);
  const toggledIndexHandler = index => {
    setToggledIndex(index);
  };

  return state.init ? (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner">
        <Formik initialValues={state.selected} onSubmit={handleSubmit}>
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
            <form
              onSubmit={handleSubmit}
              className="form__content candidate-set-tag"
            >
              {isSubmitting && <LoadMask />}

              <div className="candidate-set-tag__container">
                <div className="candidate-set-tag__avatar">
                  <Avatar size={55} name={FullName} image={PictureUrl} />
                </div>

                <div className="candidate-set-tag__content">
                  <div className="candidate-set-tag__row">
                    <div className="candidate-set-tag__person">
                      <span className="candidate-set-tag__title">
                        {FullName}
                      </span>
                    </div>
                  </div>
                  <div className="candidate-set-tag__row">
                    <div className="candidate-set-tag__info">
                      <span className="candidate-set-tag__company">
                        {isStudent ? currentSchoolName : FirmName}
                      </span>
                      {!isStudent && (
                        <span className="candidate-set-tag__school">
                          {SchoolName}
                        </span>
                      )}
                      <span className="candidate-set-tag__location">
                        {OfficeLocationName}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="form__group form__group--compact"
                style={{
                  marginBottom: 0,
                }}
              >
                {state.groups.map(group => (
                  <BasicAccordion
                    title={group.label}
                    key={group.id}
                    toggleIndex={group.id}
                    toggledIndex={toggledIndex}
                    toggleFunc={toggledIndexHandler}
                    isToggled={
                      group.tags.some(tag => values[`${_fieldPref}${tag.id}`])
                        ? true
                        : false
                    }
                  >
                    {group.tags.map(tag => {
                      return (
                        <div
                          className="candidate-set-tag__checkbox"
                          key={tag.id}
                        >
                          <BasicCheckbox
                            value={'Yes'}
                            disabled={tag.disabled}
                            singleRow
                            checked={values[`${_fieldPref}${tag.id}`] || false}
                            title={tag.label}
                            name={`${_fieldPref}${tag.id}`}
                            controlFunc={handleChange}
                            labelStyles={{
                              fontFamily: 'Noto Sans, sans-serif',
                            }}
                          />
                        </div>
                      );
                    })}
                  </BasicAccordion>
                ))}
              </div>

              <div className="form__controls form__controls--compact">
                <BasicButtonGroup>
                  <BasicSubmitButton
                    controlFunc={handleSubmit}
                    btnWidth={150}
                    btnHeight={40}
                    btnFontWeight={700}
                    btnFontSize="0.8571rem"
                    btnArrow={false}
                    btnTextColor="#27282d"
                    btnText="Save Tags"
                  />

                  <BasicButton
                    controlFunc={dismiss}
                    btnWidth={150}
                    btnHeight={40}
                    btnFontWeight={700}
                    btnFontSize="0.8571rem"
                    btnArrow={false}
                    btnTextColor="#27282d"
                    btnText="Cancel"
                  />
                </BasicButtonGroup>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </CSSTransition>
  ) : (
    <div className="form__inner">
      <div className="horizontal-loader__container">
        <div className="horizontal-loader__container">
          <HorizontalLoader />
        </div>
      </div>
    </div>
  );
};

export default CandidateSetTagForm;
